import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, RefreshCw } from 'lucide-react';
import { SideNavigationMenu } from '../components/library/side-navigation-menu/SideNavigationMenu';
import { useAuth } from '../contexts/AuthContext';
import {
  Job,
  JobAction,
  JobType,
  ParamValue,
  Schedule,
  clearJobSchedule,
  deleteJob,
  listJobTypes,
  listJobs,
  runJobAction,
  updateJobParams,
  updateJobSchedule,
} from '../services/JobMonitorService';
import { JobRow } from './jobs/JobRow';
import { LogViewerModal } from './jobs/LogViewerModal';
import { ErrorsModal } from './jobs/ErrorsModal';
import styles from './JobsPage.module.css';

const REFRESH_INTERVAL_MS = 10000;

const JobsPage: React.FC = () => {
  const { getToken } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [jobTypes, setJobTypes] = useState<Record<string, JobType>>({});
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Record<string, JobAction>>({});
  const [token, setToken] = useState<string | null>(null);
  const [logsFor, setLogsFor] = useState<Job | null>(null);
  const [errorsFor, setErrorsFor] = useState<Job | null>(null);

  const refresh = useCallback(async () => {
    setRefreshing(true);
    try {
      const accessToken = await getToken();
      if (!accessToken) throw new Error('Could not obtain an access token. Please log in again.');
      setToken(accessToken);
      const [loaded, types] = await Promise.all([listJobs(accessToken), listJobTypes(accessToken)]);
      setJobs(loaded);
      setJobTypes(Object.fromEntries(types.map((type) => [type.job_type, type])));
      setError(null);
    } catch (e) {
      setError((e as Error).message || 'Could not load jobs');
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [refresh]);

  const applyUpdatedJob = (updated: Job) =>
    setJobs((current) => current.map((job) => (job.job_id === updated.job_id ? updated : job)));

  const handleAction = async (jobId: string, action: JobAction) => {
    setBusy((current) => ({ ...current, [jobId]: action }));
    setError(null);
    try {
      applyUpdatedJob(await runJobAction(jobId, action, await requireToken()));
    } catch (e) {
      setError((e as Error).message || `Could not ${action} the job`);
    } finally {
      setBusy((current) => {
        const next = { ...current };
        delete next[jobId];
        return next;
      });
    }
  };

  const requireToken = async (): Promise<string> => {
    const accessToken = token ?? (await getToken());
    if (!accessToken) throw new Error('Could not obtain an access token. Please log in again.');
    return accessToken;
  };

  const handleSaveSchedule = async (jobId: string, schedule: Schedule, enabled: boolean) => {
    applyUpdatedJob(await updateJobSchedule(jobId, schedule, enabled, await requireToken()));
  };

  const handleClearSchedule = async (jobId: string) => {
    applyUpdatedJob(await clearJobSchedule(jobId, await requireToken()));
  };

  const handleSaveParams = async (jobId: string, params: Record<string, ParamValue>) => {
    applyUpdatedJob(await updateJobParams(jobId, params, await requireToken()));
  };

  const handleDelete = async (job: Job) => {
    setError(null);
    try {
      await deleteJob(job.job_id, await requireToken());
      setJobs((current) => current.filter((entry) => entry.job_id !== job.job_id));
    } catch (e) {
      setError((e as Error).message || 'Could not delete the job');
    }
  };

  return (
    <div className={styles.page}>
      <SideNavigationMenu isOpen={true} isCollapsed={true} lockCollapsed={true} />

      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Job Monitor</h1>
        </div>
        <div className={styles.headerActions}>
          <button type="button" className={styles.secondaryBtn} onClick={refresh} disabled={refreshing}>
            {refreshing ? <Loader2 size={14} className={styles.spin} /> : <RefreshCw size={14} />}
            Refresh
          </button>
        </div>
      </header>

      {error && <div className={styles.errorBar}>{error}</div>}

      <div className={styles.body}>
        {loading && <p className={styles.emptyState}>Loading jobs…</p>}
        {!loading && jobs.length === 0 && !error && (
          <p className={styles.emptyState}>No jobs are registered yet.</p>
        )}
        {jobs.map((job) => (
          <JobRow
            key={job.job_id}
            job={job}
            jobType={jobTypes[job.job_type] ?? null}
            busyAction={busy[job.job_id] ?? null}
            onAction={handleAction}
            onSaveSchedule={handleSaveSchedule}
            onClearSchedule={handleClearSchedule}
            onSaveParams={handleSaveParams}
            onDelete={handleDelete}
            onViewLogs={setLogsFor}
            onViewErrors={setErrorsFor}
          />
        ))}
      </div>

      {logsFor && token && (
        <LogViewerModal job={logsFor} token={token} onClose={() => setLogsFor(null)} />
      )}
      {errorsFor && token && (
        <ErrorsModal job={errorsFor} token={token} onClose={() => setErrorsFor(null)} />
      )}
    </div>
  );
};

export default JobsPage;
